import { EditorModes, getOrderedCopperLayers, matchLayerIdentifier, PcbNodeTypes, PrimitiveRole, ReviewType, } from "@buildwithflux/models";
import { Clipper } from "../../clipper";
import { box2 } from "../../common";
import { CurveDivisions, triangulateShape } from "../../fills";
import { getNodesOfType } from "../../nodes";
import { approxFittingPolygon, getConnectedCopperLayers } from "../../pcb";
import { getProblemTypeKeyFromReviewType } from "../types";
/**
 * For each protected polygon, find all different-net vias and traces that overlap with at least one of
 * its shapes on at least one of its layers, and report them as intruding nodes.
 */
export class ProtectedIntrusionsValidator {
    reviewType = ReviewType.DesignRuleCheckProtectedIntrusions;
    problemTypeKey = getProblemTypeKeyFromReviewType(this.reviewType);
    async checkForProblems(inputs) {
        try {
            const foundProblemsByUid = new Map();
            const polygonNodes = getNodesOfType(inputs.pcbLayoutNodes, PcbNodeTypes.polygon);
            const protectedPolygons = polygonNodes.filter((polygon) => polygon.bakedRules.protected ?? false);
            for (const protectedPolygon of protectedPolygons) {
                const polygonNetId = protectedPolygon.bakedRules.effectiveNetId;
                const layoutId = protectedPolygon.bakedRules.topLevelLayoutUid;
                if (!layoutId) {
                    continue;
                }
                const layoutNode = inputs.pcbLayoutNodes[layoutId];
                if (!layoutNode) {
                    continue;
                }
                const stackup = getOrderedCopperLayers(layoutNode.bakedRules.stackup);
                const container = inputs.containerMap.get(layoutId);
                if (!container) {
                    continue;
                }
                const polygonConnectedLayers = getConnectedCopperLayers(protectedPolygon, stackup);
                const polygonPrimitives = inputs.nodeShapesMap.get(protectedPolygon.uid);
                if (!polygonPrimitives) {
                    continue;
                }
                // The outline primitive of a polygon will be the same on all layers, so we only need
                // to consider the first one.
                const polygonOutlinePrimitive = polygonPrimitives.primitives.find((primitive) => primitive.role === PrimitiveRole.polygonOutline);
                if (!polygonOutlinePrimitive) {
                    continue;
                }
                const polygonOutlineClipperShape = Clipper.shape([
                    approxFittingPolygon(polygonOutlinePrimitive, CurveDivisions),
                ]);
                const polygonTriangulatedPartitions = triangulateShape(polygonOutlineClipperShape);
                for (const partition of polygonTriangulatedPartitions) {
                    const bbox = box2.fromPoints(partition);
                    if (!bbox) {
                        continue;
                    }
                    for (const potentialIntrudingNode of container.quadTree.search(box2.toRBush(bbox))) {
                        const foundProblemKey = `${this.problemTypeKey}_${protectedPolygon.uid}_${potentialIntrudingNode.nodeUid}`;
                        if (foundProblemsByUid.has(foundProblemKey)) {
                            // We already found this problem, no need to check again
                            continue;
                        }
                        const candidateIntrudingNode = inputs.pcbLayoutNodes[potentialIntrudingNode.nodeUid];
                        if (!candidateIntrudingNode) {
                            continue;
                        }
                        // We only care about nodes that are vias or route segments
                        if (candidateIntrudingNode.type !== PcbNodeTypes.via &&
                            candidateIntrudingNode.type !== PcbNodeTypes.routeSegment) {
                            continue;
                        }
                        // ...and they need to be on different nets
                        if (candidateIntrudingNode.bakedRules.effectiveNetId === polygonNetId) {
                            continue;
                        }
                        const intruderNodeGeometry = inputs.nodeShapesMap.get(candidateIntrudingNode.uid);
                        if (!intruderNodeGeometry) {
                            continue;
                        }
                        const intruderConnectedLayers = getConnectedCopperLayers(candidateIntrudingNode, stackup);
                        const commonConnectedLayers = polygonConnectedLayers.filter((layer) => intruderConnectedLayers.some((intruderLayer) => layer.uid === intruderLayer.uid));
                        if (commonConnectedLayers.length === 0) {
                            continue;
                        }
                        for (const commonConnectedLayer of commonConnectedLayers) {
                            const intruderCopperPrimitivesForLayer = intruderNodeGeometry.primitives.filter((primitive) => primitive.role === PrimitiveRole.copper &&
                                primitive.layerId &&
                                matchLayerIdentifier(commonConnectedLayer, primitive.layerId));
                            const intruderShapePoints = intruderCopperPrimitivesForLayer.map((copperPrimitive) => approxFittingPolygon(copperPrimitive, CurveDivisions));
                            if (intruderShapePoints.length === 0) {
                                // No copper primitives for this layer
                                continue;
                            }
                            const intruderClipperShape = Clipper.shape(intruderShapePoints);
                            const isIntersecting = Math.abs(polygonOutlineClipperShape.intersect(intruderClipperShape).totalArea()) > 0;
                            if (isIntersecting) {
                                foundProblemsByUid.set(foundProblemKey, {
                                    problemTypeKey: this.problemTypeKey,
                                    key: foundProblemKey,
                                    affectedItems: [
                                        { type: "pcbLayoutNode", uid: protectedPolygon.uid },
                                        { type: "pcbLayoutNode", uid: candidateIntrudingNode.uid },
                                    ],
                                    affectedViews: [EditorModes.pcb],
                                });
                                break; // No need to check other layers for this polygon
                            }
                        }
                    }
                }
            }
            return {
                error: false,
                problemTypeKey: this.problemTypeKey,
                foundProblems: Array.from(foundProblemsByUid.values()),
            };
        }
        catch (e) {
            return {
                error: `Cannot compute protected intrusions for this PCB: ${e}`,
                problemTypeKey: this.problemTypeKey,
                foundProblems: [],
            };
        }
    }
}
//# sourceMappingURL=ProtectedIntrusionsValidator.js.map